import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Plus, Edit, Trash2, Eye, Save, X, Upload, Loader2, Lock, LogOut, FileDown, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import TestimonialsAdmin from '../components/TestimonialsAdmin';
import LeadsDashboard from '../components/LeadsDashboard';
import NewsletterAdmin from '../components/NewsletterAdmin';
import PaymentsAdmin from '../components/PaymentsAdmin';
import ProspectsAdmin from '../components/ProspectsAdmin';
import RgpdRequestsAdmin from '../components/RgpdRequestsAdmin';
import TransmettreAdmin from '../components/TransmettreAdmin';
import ChangePasswordAdmin from '../components/ChangePasswordAdmin';
import ArticleAiDraft from '../components/ArticleAiDraft';
import BoatsAdmin from '../components/BoatsAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  // Inline image generation (inside the article HTML content)
  const [inlinePrompt, setInlinePrompt] = useState('');
  const [generatingInline, setGeneratingInline] = useState(false);
  const [showInlinePanel, setShowInlinePanel] = useState(false);
  const contentRef = useRef(null);

  const emptyArticle = {
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Guides',
    subcategory: '',
    image: '',
    pdf_url: '',
    pdf_name: '',
    keywords: '',
    read_time: '5 min'
  };

  const VIE_A_BORD_SUBCATEGORIES = [
    { value: 'idees', label: 'Idées & Inspirations' },
    { value: 'nouveautes', label: 'Nouveautés' },
    { value: 'juridique', label: 'Juridique' },
    { value: 'conseils', label: 'Conseils & Astuces' },
    { value: 'bons-plans', label: 'Bons Plans' },
    { value: 'vie-quotidienne', label: 'Vie Quotidienne' },
  ];

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setIsAuthenticated(true);
        fetchArticles();
      } else {
        localStorage.removeItem('admin_token');
        setLoading(false);
      }
    } catch {
      localStorage.removeItem('admin_token');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Email ou mot de passe incorrect');
      }

      localStorage.setItem('admin_token', data.token);
      setIsAuthenticated(true);
      fetchArticles();
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setPassword('');
    setArticles([]);
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/articles`);
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const data = await response.json();
      setArticles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = async (title) => {
    try {
      const response = await fetch(`${API_URL}/api/articles/generate-slug?title=${encodeURIComponent(title)}`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok || !data.slug) throw new Error('slug');
      return data.slug;
    } catch {
      return title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }
  };

  const handleTitleChange = async (e) => {
    const title = e.target.value;
    setEditingArticle(prev => ({ ...prev, title }));
    
    if (isCreating && title.length > 3) {
      const slug = await generateSlug(title);
      setEditingArticle(prev => ({ ...prev, slug }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Utiliser le nouveau système d'images persistantes
      const response = await fetch(`${API_URL}/api/images/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur upload');
      }
      
      const data = await response.json();
      setEditingArticle(prev => ({ ...prev, image: `${API_URL}${data.url}` }));
      alert('Image uploadée avec succès !');
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleGenerateImage = async () => {
    const prompt = aiPrompt.trim();
    if (prompt.length < 4) {
      alert("Décris l'image souhaitée (au moins quelques mots).");
      return;
    }
    const token = localStorage.getItem('admin_token');
    if (!token) {
      alert('Session expirée, reconnecte-toi.');
      return;
    }
    setGeneratingImage(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur génération');
      }
      const data = await response.json();
      setEditingArticle((prev) => ({ ...prev, image: `${API_URL}${data.url}` }));
      setAiPrompt('');
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setGeneratingImage(false);
    }
  };

  /**
   * Generate an AI image and insert a <figure><img><figcaption> block
   * directly inside the article's content textarea at the current
   * cursor position. Uses the same backend route as the cover image.
   */
  const handleInsertInlineImage = async () => {
    const prompt = inlinePrompt.trim();
    if (prompt.length < 4) {
      alert("Décris l'image souhaitée (au moins quelques mots).");
      return;
    }
    const token = localStorage.getItem('admin_token');
    if (!token) {
      alert('Session expirée, reconnecte-toi.');
      return;
    }
    setGeneratingInline(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur génération');
      }
      const data = await response.json();
      // Build the figure block — relative path so it works in any env
      const figureHtml = `
<figure style="margin:2rem 0;">
  <img src="${data.url}" alt="${prompt.slice(0, 80)}" loading="lazy" style="width:100%;height:auto;border-radius:0.75rem;display:block;" />
  <figcaption style="color:#888680;font-size:0.8125rem;text-align:center;margin-top:0.5rem;font-style:italic;">Légende à personnaliser</figcaption>
</figure>
`;
      // Insert at cursor position (or append at end if no cursor)
      const textarea = contentRef.current;
      const currentContent = editingArticle.content || '';
      let newContent;
      if (textarea && typeof textarea.selectionStart === 'number') {
        const pos = textarea.selectionStart;
        newContent =
          currentContent.slice(0, pos) +
          figureHtml +
          currentContent.slice(pos);
        // After React re-render, place cursor right after the inserted block
        setTimeout(() => {
          textarea.focus();
          const newPos = pos + figureHtml.length;
          textarea.setSelectionRange(newPos, newPos);
        }, 0);
      } else {
        newContent = currentContent + figureHtml;
      }
      setEditingArticle((prev) => ({ ...prev, content: newContent }));
      setInlinePrompt('');
      setShowInlinePanel(false);
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setGeneratingInline(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Veuillez sélectionner un fichier PDF');
      return;
    }

    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/images/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur upload');
      }
      
      const data = await response.json();
      setEditingArticle(prev => ({ 
        ...prev, 
        pdf_url: `${API_URL}${data.url}`,
        pdf_name: file.name
      }));
      alert('PDF uploadé avec succès !');
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSave = async () => {
    if (!editingArticle.title || !editingArticle.slug || !editingArticle.excerpt) {
      alert('Veuillez remplir les champs obligatoires (titre, slug, extrait)');
      return;
    }

    setSaving(true);
    try {
      const method = isCreating ? 'POST' : 'PUT';
      const url = isCreating 
        ? `${API_URL}/api/articles` 
        : `${API_URL}/api/articles/${editingArticle.slug}`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingArticle)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur lors de la sauvegarde');
      }

      const savedArticle = await response.json();
      await fetchArticles();
      
      // Après création, passer en mode édition pour permettre de continuer à modifier
      if (isCreating) {
        setEditingArticle(savedArticle);
        setIsCreating(false);
        alert('Article créé avec succès ! Vous pouvez continuer à le modifier.');
      } else {
        setEditingArticle(null);
        setIsCreating(false);
      }
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/articles/${slug}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Erreur lors de la suppression');
      
      await fetchArticles();
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    }
  };

  const startCreate = () => {
    setEditingArticle({ ...emptyArticle });
    setIsCreating(true);
  };

  const startEdit = (article) => {
    setEditingArticle({ ...article });
    setIsCreating(false);
  };

  const cancelEdit = () => {
    setEditingArticle(null);
    setIsCreating(false);
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Connexion Admin | validatedboat</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center px-5" data-testid="login-page">
          <div className="w-full max-w-md">
            <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-8">
              <div className="text-center mb-8">
                <div className="bg-[#d9fb06] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="text-[#1a1c1b]" size={32} />
                </div>
                <h1 className="text-2xl font-black text-[#d9fb06] uppercase">Administration</h1>
                <p className="text-[#888680] mt-2">Connectez-vous pour accéder au panneau d'administration</p>
              </div>

              <form onSubmit={handleLogin}>
                {loginError && (
                  <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg mb-4 text-center">
                    {loginError}
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-[#dfddd6] mb-2">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-3 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                    placeholder="votre@email.fr"
                    autoComplete="username"
                    autoFocus
                    data-testid="email-input"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-[#dfddd6] mb-2">Mot de passe</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-3 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                    placeholder="Entrez le mot de passe"
                    autoComplete="current-password"
                    data-testid="password-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loginLoading || !password || !email}
                  className="w-full bg-[#d9fb06] text-[#1a1c1b] py-3 rounded-full font-bold uppercase flex items-center justify-center gap-2 hover:bg-[#c4e205] transition-colors disabled:opacity-50"
                  data-testid="login-btn"
                >
                  {loginLoading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    <>
                      <Lock size={20} />
                      Se connecter
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d9fb06] animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Administration | validatedboat</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#1a1c1b]" data-testid="admin-page">
        <Header />
        
        <main className="pt-24 pb-16 px-5 md:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-black text-[#d9fb06] uppercase" data-testid="admin-title">
                Administration
              </h1>
              <div className="flex gap-4">
                {!editingArticle && (
                  <button
                    onClick={startCreate}
                    className="flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-4 py-2 rounded-full font-bold hover:bg-[#c4e205] transition-colors"
                    data-testid="create-article-btn"
                  >
                    <Plus size={20} />
                    Nouvel article
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 border border-[#888680] text-[#888680] px-4 py-2 rounded-full font-bold hover:border-red-500 hover:text-red-500 transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut size={18} />
                  Déconnexion
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-300 p-4 rounded-lg mb-6">
                {error}
              </div>
            )}

            {/* Edit/Create Form */}
            {editingArticle && (
              <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-6 mb-8" data-testid="article-form">
                <h2 className="text-xl font-bold text-[#d9fb06] mb-6">
                  {isCreating ? 'Nouvel article' : 'Modifier l\'article'}
                </h2>

                <ArticleAiDraft
                  token={localStorage.getItem('admin_token')}
                  category={editingArticle.category}
                  onDraft={async (d) => {
                    const slug = isCreating ? await generateSlug(d.title) : editingArticle.slug;
                    setEditingArticle(prev => ({
                      ...prev,
                      title: d.title,
                      slug,
                      excerpt: d.excerpt,
                      content: d.content_html,
                      keywords: d.keywords || prev.keywords,
                      read_time: d.read_time || prev.read_time,
                    }));
                  }}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[#dfddd6] mb-2">Titre *</label>
                    <input
                      type="text"
                      value={editingArticle.title}
                      onChange={handleTitleChange}
                      className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="input-title"
                    />
                  </div>
                  <div>
                    <label className="block text-[#dfddd6] mb-2">Slug *</label>
                    <input
                      type="text"
                      value={editingArticle.slug}
                      onChange={(e) => setEditingArticle(prev => ({ ...prev, slug: e.target.value }))}
                      className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      disabled={!isCreating}
                      data-testid="input-slug"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-[#dfddd6] mb-2">Extrait *</label>
                  <textarea
                    value={editingArticle.excerpt}
                    onChange={(e) => setEditingArticle(prev => ({ ...prev, excerpt: e.target.value }))}
                    rows={2}
                    className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                    data-testid="input-excerpt"
                  />
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-[#dfddd6]">Contenu</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowInlinePanel((v) => !v)}
                        className="text-xs bg-[#d9fb06] text-[#1a1c1b] px-3 py-1 rounded font-bold hover:bg-[#c4e205] transition-colors flex items-center gap-1"
                        data-testid="btn-toggle-inline-image"
                      >
                        <Sparkles size={12} />
                        Insérer une image IA
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Convertir le texte brut en HTML (paragraphes)
                          const text = editingArticle.content;
                          if (!text.includes('<p>') && !text.includes('<h')) {
                            const html = text
                              .split('\n\n')
                              .map(p => p.trim())
                              .filter(p => p)
                              .map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
                              .join('\n');
                            setEditingArticle(prev => ({ ...prev, content: html }));
                          }
                        }}
                        className="text-xs bg-[#3f4816] text-[#d9fb06] px-3 py-1 rounded hover:bg-[#4f5826] transition-colors"
                      >
                        Convertir en HTML
                      </button>
                    </div>
                  </div>

                  {/* Inline AI image generation panel */}
                  {showInlinePanel && (
                    <div
                      className="mb-3 bg-gradient-to-r from-[#d9fb06]/10 to-[#3f4816]/30 border border-[#d9fb06]/40 rounded-lg p-3"
                      data-testid="inline-image-panel"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={14} className="text-[#d9fb06]" />
                        <span className="text-[#d9fb06] text-xs font-semibold uppercase tracking-wider">
                          Générer une image et l'insérer au curseur
                        </span>
                      </div>
                      <p className="text-[#888680] text-xs mb-2">
                        Place ton curseur dans le contenu où tu veux que l'image apparaisse, puis décris-la.
                        Un bloc <code className="text-[#d9fb06]">&lt;figure&gt;</code> avec légende sera inséré automatiquement.
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={inlinePrompt}
                          onChange={(e) => setInlinePrompt(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !generatingInline) {
                              e.preventDefault();
                              handleInsertInlineImage();
                            }
                          }}
                          placeholder="Ex : sel de l'Île de Ré et citron, lumière naturelle"
                          className="flex-1 bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                          data-testid="input-inline-prompt"
                          disabled={generatingInline}
                        />
                        <button
                          type="button"
                          onClick={handleInsertInlineImage}
                          disabled={generatingInline || inlinePrompt.trim().length < 4}
                          className="bg-[#d9fb06] text-[#1a1c1b] px-4 py-2 rounded-lg font-bold text-sm hover:bg-[#c4e205] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          data-testid="btn-insert-inline-image"
                        >
                          {generatingInline ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Génération…
                            </>
                          ) : (
                            <>
                              <Sparkles size={14} />
                              Insérer
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  <textarea
                    ref={contentRef}
                    value={editingArticle.content}
                    onChange={(e) => setEditingArticle(prev => ({ ...prev, content: e.target.value }))}
                    rows={10}
                    placeholder="Collez votre texte ici puis cliquez sur 'Convertir en HTML', ou écrivez directement en HTML avec les balises <p>, <h2>, <ul>, etc."
                    className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] font-mono text-sm focus:border-[#d9fb06] focus:outline-none"
                    data-testid="input-content"
                  />
                  <p className="text-[#888680] text-xs mt-1">
                    Astuce : Séparez vos paragraphes par une ligne vide, puis cliquez sur "Convertir en HTML"
                  </p>
                  
                  {/* Aperçu du contenu */}
                  {editingArticle.content && (
                    <div className="mt-4">
                      <label className="block text-[#dfddd6] mb-2">Aperçu :</label>
                      <div 
                        className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-4 text-[#dfddd6] prose prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editingArticle.content || '') }}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-[#dfddd6] mb-2">Catégorie</label>
                    <select
                      value={editingArticle.category}
                      onChange={(e) => setEditingArticle(prev => ({
                        ...prev,
                        category: e.target.value,
                        // Reset subcategory if category changes away from Vie à bord
                        subcategory: e.target.value === 'VIE À BORD' ? (prev.subcategory || 'idees') : ''
                      }))}
                      className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="input-category"
                    >
                      <option value="Guides">Guides</option>
                      <option value="Conseils">Conseils</option>
                      <option value="Négociation">Négociation</option>
                      <option value="Actualités">Actualités</option>
                      <option value="VIE À BORD">Vie à bord</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#dfddd6] mb-2">Temps de lecture</label>
                    <input
                      type="text"
                      value={editingArticle.read_time}
                      onChange={(e) => setEditingArticle(prev => ({ ...prev, read_time: e.target.value }))}
                      className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      placeholder="5 min"
                      data-testid="input-readtime"
                    />
                  </div>
                  <div>
                    <label className="block text-[#dfddd6] mb-2">Mots-clés SEO</label>
                    <input
                      type="text"
                      value={editingArticle.keywords}
                      onChange={(e) => setEditingArticle(prev => ({ ...prev, keywords: e.target.value }))}
                      className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      placeholder="mot1, mot2, mot3"
                      data-testid="input-keywords"
                    />
                  </div>
                </div>

                {/* Vie à bord subcategory selector - only visible when category is VIE À BORD */}
                {editingArticle.category === 'VIE À BORD' && (
                  <div className="mb-4 bg-[#3f4816]/30 border border-[#3f4816] rounded-lg p-4">
                    <label className="block text-[#d9fb06] mb-2 font-semibold">
                      🌊 Thème "Vie à bord" *
                    </label>
                    <p className="text-[#888680] text-sm mb-3">
                      Choisissez le thème dans lequel cet article sera classé sur la page Vie à bord
                    </p>
                    <select
                      value={editingArticle.subcategory || 'idees'}
                      onChange={(e) => setEditingArticle(prev => ({ ...prev, subcategory: e.target.value }))}
                      className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="input-subcategory"
                    >
                      {VIE_A_BORD_SUBCATEGORIES.map(sub => (
                        <option key={sub.value} value={sub.value}>{sub.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-[#dfddd6] mb-2">Image</label>
                  <div className="bg-[#3f4816]/30 border border-[#3f4816] rounded-lg p-3 mb-3">
                    <p className="text-[#d9fb06] text-sm font-semibold mb-1">💡 Upload simple :</p>
                    <p className="text-[#888680] text-sm">
                      Cliquez sur "Uploader une image" pour ajouter votre photo. Elle sera sauvegardée et persistera après redéploiement.
                      <br/>Vous pouvez aussi utiliser une URL externe (Unsplash, Pexels).
                    </p>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={editingArticle.image}
                        onChange={(e) => setEditingArticle(prev => ({ ...prev, image: e.target.value }))}
                        className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                        placeholder="URL de l'image (automatique après upload)"
                        data-testid="input-image"
                      />
                      <div className="mt-2">
                        <label className="flex items-center gap-2 cursor-pointer text-[#d9fb06] hover:text-[#c4e205]">
                          <Upload size={18} />
                          <span>Uploader une image</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            data-testid="upload-image"
                          />
                          {uploadingImage && <Loader2 size={18} className="animate-spin" />}
                        </label>
                      </div>
                    </div>
                    {editingArticle.image && (
                      <img
                        src={editingArticle.image}
                        alt="Preview"
                        className="w-32 h-20 object-cover rounded-lg border border-[#3f4816]"
                      />
                    )}
                  </div>

                  {/* AI Image Generation */}
                  <div className="mt-4 bg-gradient-to-r from-[#d9fb06]/10 to-[#3f4816]/30 border border-[#d9fb06]/40 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={16} className="text-[#d9fb06]" />
                      <span className="text-[#d9fb06] text-sm font-semibold">
                        Générer avec l'IA (Nano Banana)
                      </span>
                    </div>
                    <p className="text-[#888680] text-xs mb-2">
                      Décris l'image souhaitée — ex. "voilier au coucher du soleil dans le port de La Rochelle".
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !generatingImage) {
                            e.preventDefault();
                            handleGenerateImage();
                          }
                        }}
                        placeholder="Décris ton image…"
                        className="flex-1 bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                        data-testid="input-ai-prompt"
                        disabled={generatingImage}
                      />
                      <button
                        type="button"
                        onClick={handleGenerateImage}
                        disabled={generatingImage || aiPrompt.trim().length < 4}
                        className="bg-[#d9fb06] text-[#1a1c1b] px-4 py-2 rounded-lg font-bold text-sm hover:bg-[#c4e205] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        data-testid="btn-generate-image"
                      >
                        {generatingImage ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Génération…
                          </>
                        ) : (
                          <>
                            <Sparkles size={16} />
                            Générer
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* PDF Upload */}
                <div className="mb-6">
                  <label className="block text-[#dfddd6] mb-2">PDF à télécharger (optionnel)</label>
                  <div className="flex gap-4 items-center">
                    <label className="flex items-center gap-2 cursor-pointer bg-[#3f4816] text-[#d9fb06] px-4 py-2 rounded-lg hover:bg-[#4f5826] transition-colors">
                      <FileDown size={18} />
                      <span>Uploader un PDF</span>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handlePdfUpload}
                        className="hidden"
                      />
                      {uploadingPdf && <Loader2 size={18} className="animate-spin" />}
                    </label>
                    {editingArticle.pdf_url && (
                      <div className="flex items-center gap-2 bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2">
                        <FileDown size={18} className="text-[#d9fb06]" />
                        <span className="text-[#dfddd6] text-sm">{editingArticle.pdf_name || 'Document.pdf'}</span>
                        <button
                          type="button"
                          onClick={() => setEditingArticle(prev => ({ ...prev, pdf_url: '', pdf_name: '' }))}
                          className="text-red-500 hover:text-red-400 ml-2"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-[#888680] text-xs mt-2">
                    Le PDF apparaîtra en bas de l'article avec un bouton "Télécharger"
                  </p>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-6 py-2 rounded-full font-bold hover:bg-[#c4e205] transition-colors disabled:opacity-50"
                    data-testid="save-article-btn"
                  >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    {isCreating ? 'Créer' : 'Enregistrer'}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="flex items-center gap-2 border border-[#888680] text-[#888680] px-6 py-2 rounded-full font-bold hover:border-[#dfddd6] hover:text-[#dfddd6] transition-colors"
                    data-testid="cancel-btn"
                  >
                    <X size={18} />
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Leads dashboard (estimations + devis) */}
            {!editingArticle && (
              <>
                <AnalyticsDashboard token={localStorage.getItem('admin_token')} />
                <ProspectsAdmin token={localStorage.getItem('admin_token')} />
                <BoatsAdmin token={localStorage.getItem('admin_token')} />
                <TestimonialsAdmin token={localStorage.getItem('admin_token')} />
                <NewsletterAdmin token={localStorage.getItem('admin_token')} />
                <PaymentsAdmin token={localStorage.getItem('admin_token')} />
                <RgpdRequestsAdmin token={localStorage.getItem('admin_token')} />
                <TransmettreAdmin token={localStorage.getItem('admin_token')} />
                <LeadsDashboard token={localStorage.getItem('admin_token')} />
                <ChangePasswordAdmin token={localStorage.getItem('admin_token')} onLogout={handleLogout} />
              </>
            )}

            {/* Articles List */}
            <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#3f4816]">
                <h2 className="text-lg font-bold text-[#dfddd6]">
                  Articles ({articles.length})
                </h2>
              </div>
              
              {articles.length === 0 ? (
                <div className="p-8 text-center text-[#888680]">
                  Aucun article. Cliquez sur "Nouvel article" pour commencer.
                </div>
              ) : (
                <div className="divide-y divide-[#3f4816]">
                  {articles.map((article) => (
                    <div
                      key={article.slug}
                      className="p-4 flex items-center gap-4 hover:bg-[#3f4816]/20 transition-colors"
                      data-testid={`article-row-${article.slug}`}
                    >
                      {article.image && (
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-20 h-14 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[#dfddd6] font-semibold truncate">
                          {article.title}
                        </h3>
                        <p className="text-[#888680] text-sm">
                          {article.category} • {article.date} • {article.read_time}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0 items-center">
                        <button
                          type="button"
                          onClick={function() { startEdit(article); }}
                          className="bg-[#d9fb06] text-[#1a1c1b] px-4 py-2 rounded-full font-bold text-sm hover:bg-[#c4e205] transition-colors"
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={function() { navigate('/blog/' + article.slug); }}
                          className="p-2 text-[#888680] hover:text-[#d9fb06] transition-colors"
                          title="Voir"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={function() { handleDelete(article.slug); }}
                          className="p-2 text-[#888680] hover:text-red-500 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Admin;
